import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Activity, CheckCircle2, Clock, XCircle, Layers } from 'lucide-react';
import StatsCard from '@/components/common/StatsCard';
import TimeRangeSelector, { TimeRange } from '@/components/common/TimeRangeSelector';
import JobsTable from '@/components/jobs/JobsTable';
import JobDetailsModal from '@/components/jobs/JobDetailsModal';
import { useJobs, useMetrics, useRetryJob } from '@/hooks/useJobs';
import { getTimeRangeTimestamp } from '@/lib/utils';
import type { Job } from '@/lib/types';

const VALID_TIME_RANGES: TimeRange[] = ['15m', '1h', '24h', '7d', '30d', 'all'];

export default function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTimeRange = searchParams.get('time_range');
  const initialTimeRange: TimeRange =
    urlTimeRange && VALID_TIME_RANGES.includes(urlTimeRange as TimeRange)
      ? (urlTimeRange as TimeRange)
      : '24h';

  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange>(initialTimeRange);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);

  // Retry job mutation
  const retryJobMutation = useRetryJob();

  // Sync time range to URL
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (timeRange === '24h') {
        params.delete('time_range');
      } else {
        params.set('time_range', timeRange);
      }
      return params;
    }, { replace: true });
  }, [timeRange, setSearchParams]);

  const timeRangeFilter = useMemo(() => getTimeRangeTimestamp(timeRange), [timeRange]);

  // Use metrics endpoint for accurate stats (SQL aggregation, no pagination limits)
  const { data: metrics, isLoading: statsLoading } = useMetrics(timeRange);

  // Compute stats from metrics response
  const stats = useMemo(() => {
    if (!metrics?.job_activity) {
      return { total: 0, pending: 0, running: 0, completed: 0, failed: 0 };
    }
    const {
      recent_pending = 0,
      recent_running = 0,
      recent_completed = 0,
      recent_failed = 0,
      recent_retry_pending = 0,
    } = metrics.job_activity;
    return {
      total: recent_pending + recent_running + recent_completed + recent_failed + recent_retry_pending,
      pending: recent_pending + recent_retry_pending,
      running: recent_running,
      completed: recent_completed,
      failed: recent_failed,
    };
  }, [metrics]);

  const { data: runningJobs, isLoading: runningLoading } = useJobs({
    status_name: 'running',
    limit: 8,
    sort_by: 'start_ts:desc',
    embed: 'assignments',
    fields: 'id,type_name,status_name,create_ts,start_ts,complete_ts,assignments(worker_id,release_ts)',
  }, timeRangeFilter);

  const { data: failedJobs, isLoading: failedLoading } = useJobs({
    status_name: 'failed',
    limit: 8,
    sort_by: 'complete_ts:desc',
    embed: 'assignments',
    fields: 'id,type_name,status_name,create_ts,start_ts,complete_ts,assignments(worker_id,release_ts)',
  }, timeRangeFilter);

  const handleJobClick = (job: Job) => {
    setSelectedJobId(job.id);
    setIsModalOpen(true);
  };

  const handleRetryJob = useCallback(async (jobId: string) => {
    setRetryingJobId(jobId);
    try {
      await retryJobMutation.mutateAsync(jobId);
      toast.success('Job queued for retry');
    } catch (error) {
      console.error('Failed to retry job:', error);
      toast.error('Failed to retry job. Please try again.');
    } finally {
      setRetryingJobId(null);
    }
  }, [retryJobMutation]);

  return (
    <div className="p-8 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Overview of job activity and system status
            </p>
          </div>
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatsCard
            label="Total Jobs"
            value={statsLoading ? '-' : stats.total.toLocaleString()}
            icon={Layers}
            variant="default"
          />
          <StatsCard
            label="Pending"
            value={statsLoading ? '-' : stats.pending.toLocaleString()}
            icon={Clock}
            variant="warning"
          />
          <StatsCard
            label="Running"
            value={statsLoading ? '-' : stats.running.toLocaleString()}
            icon={Activity}
            variant="primary"
          />
          <StatsCard
            label="Completed"
            value={statsLoading ? '-' : stats.completed.toLocaleString()}
            icon={CheckCircle2}
            variant="success"
          />
          <StatsCard
            label="Failed"
            value={statsLoading ? '-' : stats.failed.toLocaleString()}
            icon={XCircle}
            variant="error"
          />
        </div>

        {/* Active Jobs */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Active Jobs</h2>
              <p className="text-sm text-muted-foreground">
                Currently running jobs ({runningJobs?.items?.length || 0})
              </p>
            </div>
          </div>
          <div className="glass-card rounded-lg overflow-hidden">
            <JobsTable
              jobs={runningJobs?.items || []}
              loading={runningLoading}
              onJobClick={handleJobClick}
            />
          </div>
        </div>

        {/* Recent Failures */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Recent Failures</h2>
              <p className="text-sm text-muted-foreground">
                Last 8 failed jobs
              </p>
            </div>
          </div>
          <div className="glass-card rounded-lg overflow-hidden">
            <JobsTable
              jobs={failedJobs?.items || []}
              loading={failedLoading}
              onJobClick={handleJobClick}
              onRetryJob={handleRetryJob}
              retryingJobId={retryingJobId}
            />
          </div>
        </div>

        {/* Job Details Modal */}
        {selectedJobId && (
          <JobDetailsModal
            key={selectedJobId}
            jobId={selectedJobId}
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedJobId(null);
            }}
          />
        )}
      </div>
  );
}
